import { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import StoreIcon from '@mui/icons-material/Store';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import axiosInstance from '../../lib/axios';
import type { Location } from '../../types';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';

const fetchLocation = async (id: string): Promise<Location> => {
  const response = await axiosInstance.get(`/locations/${id}`);
  return response.data;
};

export const LocationShow = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { mode } = useContext(ColorModeContext);
  const animations = useAnimationStyles();

  const { data: location, isLoading, error } = useQuery({
    queryKey: ['location', id],
    queryFn: () => fetchLocation(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !location) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error loading location</Typography>
      </Box>
    );
  }

  const InfoItem = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body1">{value || '-'}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 3, ...animations.fadeIn }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/app/locations')}
            sx={{ color: palette.textSecondary }}
          >
            Back
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {location.type === 'store' ? (
              <StoreIcon sx={{ color: palette.primary, fontSize: 32 }} />
            ) : (
              <WarehouseIcon sx={{ color: palette.accent, fontSize: 32 }} />
            )}
            <Typography variant="h4" sx={{ fontWeight: typography.fontWeight.semibold }}>
              {location.name}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/app/locations/${id}/edit`)}
          sx={{
            bgcolor: palette.primary,
            '&:hover': { bgcolor: alpha(palette.primary, 0.9) },
          }}
        >
          Edit
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ bgcolor: mode === 'dark' ? palette.surface : palette.light.surface }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Location Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoItem label="Type" value={
                    <Chip
                      label={location.type}
                      size="small"
                      sx={{
                        bgcolor: alpha(location.type === 'store' ? palette.primary : palette.accent, 0.1),
                        color: location.type === 'store' ? palette.primary : palette.accent,
                        textTransform: 'capitalize',
                      }}
                    />
                  } />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label="Code" value={location.code} />
                </Grid>
                <Grid item xs={12}>
                  <InfoItem label="Description" value={location.description} />
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Address
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InfoItem label="Address" value={location.address} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem label="City" value={location.city} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem label="Wilaya" value={location.wilaya} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <InfoItem label="Zip Code" value={location.zipCode} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label="Country" value={location.country} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label="Currency" value={location.currency} />
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Contact
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoItem label="Phone" value={location.phone} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem label="Email" value={location.email} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: mode === 'dark' ? palette.surface : palette.light.surface }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status & Capabilities
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <InfoItem
                label="Status"
                value={
                  <Chip
                    label={location.isActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={location.isActive ? 'success' : 'default'}
                  />
                }
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary" gutterBottom>
                  Capabilities
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                  <Chip
                    label="Can sell to customers"
                    size="small"
                    color={location.canSellToCustomers ? 'primary' : 'default'}
                    variant={location.canSellToCustomers ? 'filled' : 'outlined'}
                  />
                  <Chip
                    label="Can fulfill online orders"
                    size="small"
                    color={location.canFulfillOnlineOrders ? 'primary' : 'default'}
                    variant={location.canFulfillOnlineOrders ? 'filled' : 'outlined'}
                  />
                  <Chip
                    label="Can receive transfers"
                    size="small"
                    color={location.canReceiveTransfers ? 'primary' : 'default'}
                    variant={location.canReceiveTransfers ? 'filled' : 'outlined'}
                  />
                  <Chip
                    label="Can send transfers"
                    size="small"
                    color={location.canSendTransfers ? 'primary' : 'default'}
                    variant={location.canSendTransfers ? 'filled' : 'outlined'}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: mode === 'dark' ? palette.surface : palette.light.surface, mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Timestamps
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <InfoItem
                label="Created"
                value={new Date(location.createdAt).toLocaleDateString()}
              />
              <InfoItem
                label="Last Updated"
                value={new Date(location.updatedAt).toLocaleDateString()}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LocationShow;
